import React from "react";
import _ from "lodash";
import clsx from "clsx";

import {connect} from "react-redux";

import {makeStyles} from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import {CircularProgress, Badge} from "@material-ui/core";

import TabsContainer from "../../../../components/TabsContainer";
import ScrollableContainer from "../../../../components/ScrollableContainer";

import {TRANSACTION_TYPE_VALUES} from "../../constants";
import {updatePortfolioCombinedTradingOption} from "../../../../components/TradingStore/actions";

// Sub-components
import PortfolioCard from "./components/CombinedTrade/PortfolioCard";
import PortfolioTabContent from "./components/CombinedTrade/PortfolioTabContent";
import PortfolioBuilderFeature from "./components/PortfolioBuilderFeature";
import RebalancingFeature from "./components/RebalancingFeature";

// Utilities & hooks
import {
  getPortfolioId,
  getPortfolioTabs,
  getDefaultActiveTab,
  portfolioHasErrors,
  getTabErrors, portfolioFirstError,
} from "./components/CombinedTrade/utils";
import useDataInitialization from "./components/CombinedTrade/useDataInitialization";
import {portfolioTransactionAmount, portfolioTransactionsAmount} from "../../utils";
import {withEuroOrDash} from "../../../../utils/utils";
import AmountChip from "../../../CustomerDashboardV2/components_v2/AmountChip";

import styles from "./styles";

// Re-export for backward compatibility (used by PortfolioTradeV2 hooks)
export {getPortfolioInstrumentsData, portfolioHasTransactions} from "./components/CombinedTrade/dataUtils";

const useStyles = makeStyles(styles);

const LoadingState = () => {
  const classes = useStyles();
  return (
    <div className={classes.loadingContainer}>
      <CircularProgress className={classes.loadingIndicator}/>
    </div>
  );
};

const TabLabel = ({selectedPortfolio, tab}) => {
  const classes = useStyles();

  const count = _.get(selectedPortfolio, ['transactions', tab.key], []).length;

  if(count === 0){
    return tab.label;
  }

  const isSwitch =  (tab.key === TRANSACTION_TYPE_VALUES.SWITCH || tab.key === TRANSACTION_TYPE_VALUES.SWITCH_PLAN)

  return (
    <Badge badgeContent={count} color="primary" className={classes.tabBadge}>
      {tab.label}
      {!isSwitch && (
        <span className={classes.tabTotal}>
          ({withEuroOrDash(portfolioTransactionAmount(selectedPortfolio, tab.key))})
        </span>
      )}
    </Badge>
  )
}

const CombinedTradeStepV2 = props => {
  const classes = useStyles();

  const {
    onAnswerChange,
    questions: [transactionsQuestion],
    dataService,
    isVirtual,
    isNewVirtual,
    showSectionsWithoutTransactions,
    showTotal,
    showTradingActions,
  } = props;

  const portfolios = React.useMemo(() => {
    const filled = transactionsQuestion.answer || [];

    if(_.isEmpty(filled)) return transactionsQuestion.builtPortfolios;

    const groupedById = _.keyBy(filled, 'portfolioId');
    const unfilled = transactionsQuestion.builtPortfolios.filter(p => !groupedById[p.portfolioId]);

    return [...filled, ...unfilled];
  }, [transactionsQuestion.answer, transactionsQuestion.builtPortfolios]);

  const [selectedPortfolioId, setSelectedPortfolioId] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState(TRANSACTION_TYPE_VALUES.BUY);

  const handlePortfolioChange = (portfolio, changes) => {
    let updatedPortfolios;

    if (portfolio && changes) {
      updatedPortfolios = portfolios.map(p => {
        if(getPortfolioId(p, isVirtual) === getPortfolioId(portfolio, isVirtual)){
          const updated = {...p, ...changes}

          props.dispatch(updatePortfolioCombinedTradingOption(dataService.customer_id, updated.portfolioId, {
            transactions: updated.transactions,
            depot_fee: updated.depot_fee,
            discount: updated.discount
          }));

          return updated;
        }

        return p;
      });
    } else {
      // Legacy: mutations already applied externally, clear errors on all
      updatedPortfolios = portfolios.map(p => {
        p.errors = null;
        p.trading_errors = null;
        p.savings_plan_errors = null;
        p.payout_plan_errors = null;
        p.switch_plan_errors = null;
        props.dispatch(updatePortfolioCombinedTradingOption(dataService.customer_id, p.portfolioId, {
          transactions: p.transactions,
          depot_fee: p.depot_fee,
          discount: p.discount
        }));
        return p;
      });
    }

    onAnswerChange(transactionsQuestion.uid, updatedPortfolios);
  };

  const handlePortfolioSelect = (portfolio) => {
    setSelectedPortfolioId(getPortfolioId(portfolio, isVirtual));
    const tabs = getPortfolioTabs(portfolio, isVirtual, props.allowedPortfolioTradeOptions);
    const defaultTab = getDefaultActiveTab(portfolio, tabs);
    if (defaultTab) {
      setActiveTab(defaultTab);
    }
  };

  const {defaults, setDefaults, loading} = useDataInitialization({
    portfolios,
    dataService,
    isVirtual,
    handlePortfolioSelect,
  });

  const selectedPortfolio = React.useMemo(() => {
    return portfolios.find(p => getPortfolioId(p, isVirtual) === selectedPortfolioId);
  }, [selectedPortfolioId, portfolios]);

  const updateDefaults = React.useCallback((data, key) => {
    setDefaults((_defaults) => ({..._defaults, [key]: data}));
  }, [setDefaults]);

  // --- Derived data ---

  const availableTabs = React.useMemo(() => {
    return getPortfolioTabs(selectedPortfolio, isVirtual, props.allowedPortfolioTradeOptions);
  }, [selectedPortfolio, isVirtual]);

  const availableTradeOptions = React.useMemo(() => {
    return availableTabs.map(tab => tab.key);
  }, [availableTabs]);

  const tabErrors = React.useMemo(() => {
    return getTabErrors(selectedPortfolio);
  }, [
    selectedPortfolio,
    selectedPortfolio && selectedPortfolio.trading_errors,
    selectedPortfolio && selectedPortfolio.savings_plan_errors,
    selectedPortfolio && selectedPortfolio.payout_plan_errors,
    selectedPortfolio && selectedPortfolio.switch_plan_errors,
  ]);

  const tabsForContainer = React.useMemo(() => {
    return availableTabs.map(tab => {
      return {
        id: tab.key,
        label: <TabLabel selectedPortfolio={selectedPortfolio} tab={tab} />,
        className: tabErrors[tab.key] ? classes.tabError : undefined,
      };
    });
  }, [availableTabs, tabErrors, classes.tabError, classes.tabBadge, selectedPortfolio && selectedPortfolio.transactions]);

  const totalAmount = React.useMemo(() => {
    return portfolios.reduce((sum, portfolio) => sum + portfolioTransactionsAmount(portfolio), 0);
  }, [portfolios]);

  // --- Auto-navigate to first error ---

  const portfolioCardRefs = React.useRef({});
  const validationTrigger = transactionsQuestion.lastValidationFailedAt;

  React.useEffect(() => {
    if (!validationTrigger) return;

    const firstErrorPortfolio = portfolios.find(p => portfolioHasErrors(p));
    if (!firstErrorPortfolio) return;

    const errorPortfolioId = getPortfolioId(firstErrorPortfolio, isVirtual);
    setSelectedPortfolioId(errorPortfolioId);

    const firstErrorKey = Object.keys(getTabErrors(firstErrorPortfolio))[0];
    if (firstErrorKey) {
      setActiveTab(firstErrorKey);
    }
  }, [validationTrigger]);

  React.useEffect(() => {
    const cardEl = portfolioCardRefs.current[selectedPortfolioId];
    if (cardEl) {
      setTimeout(() => {
        cardEl.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'center'});
      }, 50);
    }
  }, [selectedPortfolioId])

  // --- Render ---

  if (loading) {
    return <LoadingState />;
  }

  const selectedPtfError = portfolioFirstError(selectedPortfolio);

  return (
    <Grid container className="container-max-width-large" spacing={2}>
      <Grid item xs={12}>
        <AmountChip
          total={totalAmount}
          customClasses={classes}
          subLine={<span className={classes.totalValueChipSubHeader}>Gesamt</span>}
        />
      </Grid>
      <Grid item xs={12}>
        <ScrollableContainer>
          {portfolios.map((portfolio) => (
            <div
              key={getPortfolioId(portfolio, isVirtual)}
              className={classes.portfolioCardItem}
              ref={el => { portfolioCardRefs.current[getPortfolioId(portfolio, isVirtual)] = el; }}
            >
              <PortfolioCard
                portfolio={portfolio}
                selected={getPortfolioId(portfolio, isVirtual) === selectedPortfolioId}
                onClick={() => handlePortfolioSelect(portfolio)}
                isVirtual={isVirtual}
                hasError={portfolioHasErrors(portfolio)}
                handlePortfolioChange={handlePortfolioChange}
                dataService={dataService}
              />
            </div>
          ))}
        </ScrollableContainer>
        {!!selectedPtfError && (
          <div className={clsx(classes.error, 'Mui-error')}>
            {selectedPtfError}
          </div>
        )}
      </Grid>

      {/* Action buttons */}
      {showTradingActions && !isVirtual && (
        <Grid item xs={12}>
          <Grid container spacing={2} className={classes.actionButtonsContainer}>
            <RebalancingFeature
              portfolio={selectedPortfolio}
              dataService={dataService}
              isVirtual={isVirtual}
              handlePortfolioChange={handlePortfolioChange}
              allowedTradeOptions={availableTradeOptions}
            />
            <PortfolioBuilderFeature
              portfolio={selectedPortfolio}
              dataService={dataService}
              isVirtual={isVirtual}
              handlePortfolioChange={handlePortfolioChange}
              allowedTradeOptions={availableTradeOptions}
            />
          </Grid>
        </Grid>
      )}

      {/* Tabs + content */}
      <Grid item xs={12}>
        <TabsContainer
          value={activeTab}
          onChange={(e, value) => setActiveTab(value)}
          tabs={tabsForContainer}
          tabContent={
            <PortfolioTabContent
              activeTab={activeTab}
              portfolio={selectedPortfolio}
              handlePortfolioChange={handlePortfolioChange}
              minDepositDate={transactionsQuestion.minDepositDate}
              dataService={dataService}
              isVirtual={isVirtual}
              isNewVirtual={isNewVirtual}
              defaults={defaults}
              updateDefaults={updateDefaults}
              showSectionsWithoutTransactions={showSectionsWithoutTransactions}
              showTotal={showTotal}
              showTradingActions={showTradingActions}
              isCustomerApp={props.isCustomerApp}
              allowedPortfolioTradeOptions={props.allowedPortfolioTradeOptions}
              resource={props.resource}
            />
          }
        />
      </Grid>
    </Grid>
  );
};

CombinedTradeStepV2.defaultProps = {
  showSectionsWithoutTransactions: true,
  showTradingActions: true,
};

export default connect()(CombinedTradeStepV2);
